import React, {useCallback, useRef, useState} from "react";
import {CheckCircle, Cloud, Error as ErrorIcon} from "@mui/icons-material";
import {Box, Button, DialogActions, DialogContent, Grid, InputAdornment, TextField, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getPackageInternalUrl} from "../../../constants/appRoutes";
import AlertDialog from "../../alert-dialog";
import CustomDialog from "../../custom-dialog";
import FullscreenDialog from "../../fullscreen-dialog";
import ViewerWmsMap from "../../viewer-wms-map";
import EasyLoadingForm from "../EasyLoadingForm";
import {clearCatalog} from "../../../state/catalog/catalogActions";
import {moduleConfigsSelector} from "../../../state/module-configs/moduleConfigsSelectors";
import {nodeCodeSelector, nodeIdSelector, nodeSelector} from "../../../state/node/nodeSelectors";
import {
  hideEasyLoadingWmsModal,
  setEasyLoadingDataset,
  setEasyLoadingResource,
  submitEasyLoading
} from "../../../state/package-manager/packageManagerActions";
import {easyLoadingSelector, packageManagerSelector} from "../../../state/package-manager/packageManagerSelectors";
import {addSpinnerMessage, markSpinnerMessage} from "../../../state/spinner/spinnerActions";

// Helper to build a WMS GetCapabilities URL from a service URL
const buildWmsCapabilitiesUrl = (serviceUrl, proxyUrl) => {
  const input = (serviceUrl || "").trim();
  if (!input) return "";

  const sep = input.includes("?") ? "&" : "?";
  const fullUrl = `${input}${sep}REQUEST=GetCapabilities&SERVICE=WMS`;

  return proxyUrl ? `${proxyUrl}?url=${encodeURIComponent(fullUrl)}` : fullUrl;
};

const EasyWmsLoading = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const easyLoadingFormRef = useRef();

  const node = useSelector(nodeSelector);
  const nodeId = useSelector(nodeIdSelector);
  const nodeCode = useSelector(nodeCodeSelector);

  const proxyUrl = useSelector(moduleConfigsSelector)?.configs?.["viewer-wms-map"]?.proxyUrl;

  const [isConnected, setIsConnected] = useState(null);
  const [isPreviewVisible, setPreviewVisibility] = useState(false);

  const [capabilitiesInfo, setCapabilitiesInfo] = useState(null);
  const [isOverwriteDialogOpen, setIsOverwriteDialogOpen] = useState(false);

  const {packageId, resourceId, wmsIsVisible: isVisible, wmsIsImported} = useSelector(packageManagerSelector);
  const handleClose = () => {
    setIsConnected(null);
    setPreviewVisibility(false);
    setCapabilitiesInfo(null);
    setIsOverwriteDialogOpen(false);
    dispatch(hideEasyLoadingWmsModal());
  };
  const handleSubmit = () => {
    if (
      // dataset required fields
      !dataset ||
      !dataset.title ||
      !dataset.description ||
      !dataset.organization ||
      // resource required fields
      !resource ||
      !resource.url
    ) {
      setDataset({...dataset, hasError: true});
      setResource({...resource, hasError: true});
      if (!dataset.description || !dataset.organization) easyLoadingFormRef.current.setExpanded(true);
      return;
    } else {
      setDataset({...dataset, hasError: false});
      setResource({...resource, hasError: false});
      dispatch(submitEasyLoading(nodeId, node.type, dataset, resource));
    }
  };

  const {dataset, resource} = useSelector(easyLoadingSelector);
  const setDataset = useCallback(dataset => dispatch(setEasyLoadingDataset(dataset)), [dispatch]);
  const setResource = useCallback(resource => dispatch(setEasyLoadingResource(resource)), [dispatch]);

  const applyCapabilitiesToState = useCallback(
    (info, forceOverwrite = false) => {
      if (!info) return;

      const willOverwriteDatasetTitle = !!dataset?.title;
      const willOverwriteDatasetDesc = !!dataset?.description;
      const willOverwriteDatasetKeywords = !!(dataset?.keywords && dataset.keywords.length);
      const willOverwriteResourceTitle = !!resource?.title;

      const needConfirm =
        !forceOverwrite &&
        (willOverwriteDatasetTitle ||
          willOverwriteDatasetDesc ||
          willOverwriteDatasetKeywords ||
          willOverwriteResourceTitle);

      if (needConfirm) {
        setIsOverwriteDialogOpen(true);
        return;
      }

      setDataset({
        ...dataset,
        title: forceOverwrite || !dataset?.title ? info.title || "" : dataset.title,
        description: forceOverwrite || !dataset?.description ? info.description || "" : dataset.description,
        keywords:
          forceOverwrite || !(dataset?.keywords && dataset.keywords.length) ? info.keywords || [] : dataset.keywords
      });

      setResource({
        ...resource,
        title: forceOverwrite || !resource?.title ? info.title || "" : resource.title
      });
    },
    [dataset, resource, setDataset, setResource]
  );

  const confirmOverwrite = () => {
    setIsOverwriteDialogOpen(false);
    applyCapabilitiesToState(capabilitiesInfo, true);
  };
  const cancelOverwrite = () => {
    setIsOverwriteDialogOpen(false);
  };

  const fetchCapabilities = useCallback(
    serviceUrl => {
      dispatch(
        addSpinnerMessage(
          "easy-wms-preview/get-capabilities",
          t("modules.packageManager.easyWmsLoading.messages.fetchingCapabilities")
        )
      );
      fetch(buildWmsCapabilitiesUrl(serviceUrl, proxyUrl))
        .then(async response => {
          // Fail early on HTTP errors
          if (!response.ok) {
            throw new Error(`WMS GetCapabilities request failed: HTTP ${response.status}`);
          }
          try {
            const text = await response.text();
            const doc = new DOMParser().parseFromString(text, "application/xml");

            // Check response
            const capabilitiesNode = doc.querySelector("WMS_Capabilities") || doc.querySelector("wms_capabilities");
            if (!capabilitiesNode) {
              throw new Error("Invalid WMS GetCapabilities response: missing WMS_Capabilities node");
            }

            // Service section
            const serviceNode = doc.querySelector("Service") || doc.querySelector("service");

            // Title
            const serviceTitle = (
              serviceNode?.querySelector("Title")?.textContent ||
              serviceNode?.querySelector("title")?.textContent ||
              ""
            ).trim();

            // Keywords
            const keywordNodes =
              serviceNode?.querySelectorAll("KeywordList > Keyword") ||
              serviceNode?.querySelectorAll("keywordlist > keyword") ||
              [];
            const keywords = Array.from(keywordNodes)
              .map(k => k.textContent?.trim())
              .filter(Boolean);

            // Service Abstract
            const serviceAbstract = (
              serviceNode?.querySelector("Abstract")?.textContent ||
              serviceNode?.querySelector("abstract")?.textContent ||
              ""
            ).trim();

            // Layers: use standard structure WMS_Capabilities > Capability > Layer (root) > Layer (children)
            const capabilityNode = doc.querySelector("Capability") || doc.querySelector("capability");
            const rootLayer = capabilityNode
              ? capabilityNode.querySelector("Layer") || capabilityNode.querySelector("layer")
              : null;

            // Select only direct children of the root Layer (exclude the root itself)
            let layerNodes = [];
            if (rootLayer) {
              // Prefer :scope for direct children, with fallback filter
              const scoped = rootLayer.querySelectorAll(":scope > Layer, :scope > layer");
              if (scoped && scoped.length) {
                layerNodes = Array.from(scoped);
              } else {
                // Fallback: get all and keep only those whose parent is rootLayer
                layerNodes = Array.from(rootLayer.querySelectorAll("Layer, layer")).filter(
                  ln => ln.parentElement === rootLayer
                );
              }
            }

            const layerDescriptions = layerNodes
              .map(layer => {
                const lt = (
                  layer.querySelector("Title")?.textContent ||
                  layer.querySelector("title")?.textContent ||
                  ""
                ).trim();
                const la = (
                  layer.querySelector("Abstract")?.textContent ||
                  layer.querySelector("abstract")?.textContent ||
                  ""
                ).trim();
                return lt ? `- ${lt}${la ? `: ${la}` : ""}` : null;
              })
              .filter(Boolean);

            // Build final description
            const descriptionParts = [];
            if (serviceAbstract) {
              descriptionParts.push(serviceAbstract + "\n");
            }
            if (layerDescriptions.length) {
              descriptionParts.push(t("modules.packageManager.easyLoadingForm.fields.description.content.layersTitle"));
              descriptionParts.push(...layerDescriptions);
            }
            const description = descriptionParts.join("\n");

            const info = {
              title: serviceTitle,
              description: description,
              keywords: keywords
            };

            setCapabilitiesInfo(info);
            applyCapabilitiesToState(info, false);

            dispatch(markSpinnerMessage("easy-wms-preview/get-capabilities"));
            setIsConnected(true);
          } catch (error) {
            console.error(error);

            setCapabilitiesInfo(null);

            dispatch(markSpinnerMessage("easy-wms-preview/get-capabilities", true));
            setIsConnected(false);
          }
        })
        .catch(error => {
          console.error(`WMS GetCapabilities request failed ${error}`);

          setCapabilitiesInfo(null);

          dispatch(markSpinnerMessage("easy-wms-preview/get-capabilities", true));
          setIsConnected(false);
        });
    },
    [proxyUrl, dispatch, t, applyCapabilitiesToState]
  );

  return (
    <CustomDialog
      title={t("modules.packageManager.easyWmsLoading.dialog.title")}
      className="easy-wms-loading"
      open={isVisible}
      onClose={handleClose}
    >
      {isVisible && (
        <DialogContent>
          <Box sx={{marginTop: "8px"}}>
            <EasyLoadingForm
              ref={easyLoadingFormRef}
              nodeId={nodeId}
              dataset={dataset}
              setDataset={setDataset}
              resource={resource}
              setResource={setResource}
              sourceField={
                <TextField
                  className="package-manager__easy-loading-form__wms-url-field"
                  label={t("modules.packageManager.easyLoadingForm.fields.wmsUrl.label")}
                  variant="outlined"
                  value={resource?.url ?? ""}
                  onChange={e => {
                    setResource({...resource, url: e.target.value});
                    setIsConnected(null);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="start" sx={{marginLeft: "8px"}}>
                          <Tooltip title={t("modules.packageManager.easyLoadingForm.fields.wmsUrl.connect.title")}>
                            <Button
                              endIcon={
                                isConnected === null ? (
                                  <Cloud />
                                ) : isConnected ? (
                                  <CheckCircle color="success" />
                                ) : (
                                  <ErrorIcon color="error" />
                                )
                              }
                              variant="outlined"
                              component="span"
                              onClick={() => fetchCapabilities(resource.url)}
                              disabled={(resource?.url ?? "").length === 0}
                            >
                              {t("modules.packageManager.easyLoadingForm.fields.wmsUrl.connect.label")}
                            </Button>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }
                  }}
                  error={resource?.hasError && !resource.url}
                  helperText={
                    resource?.hasError &&
                    !resource.url &&
                    t("modules.packageManager.easyLoadingForm.errors.invalidWmsUrl")
                  }
                  required
                  fullWidth
                />
              }
            />
          </Box>
        </DialogContent>
      )}
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid>
            {isConnected && (
              <Button onClick={() => setPreviewVisibility(true)}>
                {t("modules.packageManager.easyWmsLoading.dialog.actions.wmsPreview.label")}
              </Button>
            )}
          </Grid>
          <Grid>
            <Button className="easy-wms-loading__actions__cancel-btn" onClick={handleClose}>
              {t("commons.confirm.cancel")}
            </Button>
            <Button className="easy-wms-loading__actions__submit-btn" onClick={handleSubmit}>
              {t("modules.packageManager.easyWmsLoading.dialog.actions.create.label")}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>

      <FullscreenDialog
        title={
          (resource?.title || "").length > 0
            ? resource.title
            : t("modules.packageManager.easyWmsLoading.previewDialog.title")
        }
        open={isPreviewVisible}
        onClose={() => setPreviewVisibility(false)}
      >
        <DialogContent>
          <ViewerWmsMap serviceUrl={resource?.url} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewVisibility(false)}>{t("commons.confirm.close")}</Button>
        </DialogActions>
      </FullscreenDialog>

      <AlertDialog
        title={t("modules.packageManager.easyWmsLoading.overwriteConfirmDialog.title")}
        open={isOverwriteDialogOpen}
        onClose={cancelOverwrite}
      >
        <DialogContent>{t("modules.packageManager.easyWmsLoading.overwriteConfirmDialog.content")}</DialogContent>
        <DialogActions>
          <Button onClick={cancelOverwrite}>{t("commons.confirm.cancel")}</Button>
          <Button onClick={confirmOverwrite}>{t("commons.confirm.submit")}</Button>
        </DialogActions>
      </AlertDialog>

      <AlertDialog
        title={t("modules.packageManager.easyWmsLoading.reportDialog.title")}
        open={wmsIsImported === true}
        onClose={handleClose}
      >
        <DialogActions>
          <Button
            onClick={() => {
              handleClose();
              dispatch(clearCatalog());
            }}
          >
            {t("commons.confirm.close")}
          </Button>
          <Button
            onClick={() => {
              handleClose();
              dispatch(clearCatalog());
              navigate(getPackageInternalUrl(nodeCode, [], packageId, resourceId));
            }}
          >
            {t("modules.packageManager.easyWmsLoading.reportDialog.actions.navigate")}
          </Button>
        </DialogActions>
      </AlertDialog>
    </CustomDialog>
  );
};

export default EasyWmsLoading;
